import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { Box, Flex, IconButton } from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSwitchInput } from 'components/common/form/switch';
import { CommonTextInput } from 'components/common/form/text';
import { PresetTrainingContext } from 'components/machine/dialogs/preset-training/context';
import { AimingContext } from 'contexts/aiming.context';
import { MachineContext } from 'contexts/machine.context';
import { getSampleSizes } from 'enums/training.enums';
import useLatest from 'hooks/useLatestRef';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  MAX_ADJUSTMENTS,
  PRECISE_PT_MODES,
  PresetTrainingMode,
} from 'lib_ts/enums/training.enums';
import { IPresetOption } from 'lib_ts/interfaces/training/i-preset-training-spec';
import { useContext, useEffect, useMemo, useState } from 'react';

const ADJUSTMENTS_OPTIONS = ArrayHelper.getIntegerOptions(0, MAX_ADJUSTMENTS);

const THRESHOLD_SPEED_MPH = 0.5;
const THRESHOLD_SPINS_RPM = 250;
const THRESHOLD_BREAKS_IN = 4;

// this spaces the icon properly vertically
const WarningIcon = (props: { content: string }) => (
  <IconButton
    size="1"
    variant="soft"
    className="btn-floating"
    color={RADIX.COLOR.WARNING}
    title={props.content}
  >
    <ExclamationTriangleIcon />
  </IconButton>
);

export const PresetForm = (props: {
  preset: IPresetOption;
  disabled: boolean;
}) => {
  const { disabled } = props;

  const { mode, spec } = props.preset;

  const { machine, unitFactors } = useContext(MachineContext);
  const { pitchTraining } = useContext(AimingContext);
  const { updateSpec } = useContext(PresetTrainingContext);

  const SAMPLE_SIZE_OPTIONS = useMemo(
    () => getSampleSizes(machine.training_threshold),
    [machine.training_threshold]
  );

  const safePriority = pitchTraining?.priority ?? BuildPriority.Default;

  const checkThresholds = mode === PresetTrainingMode.Custom;

  const showPrecisionInputs = PRECISE_PT_MODES.includes(mode);

  const [speedText, setSpeedText] = useState('');
  const [breaksText, setBreaksText] = useState('');

  const latestSpec = useLatest(spec);

  // when toggling metric or spec changes, update the text values
  useEffect(() => {
    const speedValue =
      latestSpec.current.deltaSpeedMPH * unitFactors.fromSpeedMph;
    setSpeedText(speedValue.toString());

    const breaksValue =
      latestSpec.current.deltaBreaksInches * unitFactors.fromBreaksIn;
    setBreaksText(breaksValue.toString());
  }, [
    unitFactors,
    mode, // don't trigger on spec change to allow for decimals
  ]);

  return (
    <CommonFormGrid columns={5} gap="1">
      <Box gridColumn="span 4">
        <CommonInputLabel label="tr.sample-shots" />
      </Box>

      <CommonSelectInput
        id="precision-training-samples"
        name="maxSamples"
        options={SAMPLE_SIZE_OPTIONS}
        value={spec.maxSamples.toString()}
        onNumericChange={(v) => {
          if (v <= 0) {
            return;
          }

          updateSpec({ maxSamples: v });
        }}
        disabled={disabled}
        skipSort
        size="1"
      />

      {showPrecisionInputs && (
        <>
          <Box gridColumn="span 4">
            <CommonInputLabel label="tr.max-adjustments" />
          </Box>

          <CommonSelectInput
            id="precision-training-adjustments"
            name="adjustments"
            options={ADJUSTMENTS_OPTIONS}
            value={spec.maxAdjustments.toString()}
            onNumericChange={(v) => {
              if (v < 0) {
                return;
              }

              updateSpec({ maxAdjustments: v });
            }}
            disabled={disabled}
            skipSort
            size="1"
          />
        </>
      )}

      {showPrecisionInputs && (
        <>
          <Flex gridColumn="span 4" justify="between" align="center">
            <CommonInputLabel
              label={
                machine.isMetric
                  ? 'tr.max-speed-delta-kph'
                  : 'tr.max-speed-delta-mph'
              }
            />

            {checkThresholds && spec.deltaSpeedMPH < THRESHOLD_SPEED_MPH && (
              <WarningIcon
                content={
                  `The threshold for speed that you have entered might be difficult or impossible to reproduce. ` +
                  `Please consider increasing speed delta to at least ` +
                  `${Math.ceil(
                    THRESHOLD_SPEED_MPH * unitFactors.fromSpeedMph
                  ).toFixed(0)} ` +
                  `${MetricHelper.getSpeedUnit(machine.isMetric)}.`
                }
              />
            )}
          </Flex>

          <CommonTextInput
            id="precision-training-max-speed"
            type="number"
            name="deltaSpeedMPH"
            value={speedText}
            title="Provide a positive number"
            onChange={(v) => setSpeedText(v ?? '')}
            onNumericChange={(v) => {
              // * this conversion is in reverse b/c the user is inputting in their own units
              const value = v / unitFactors.fromSpeedMph;
              updateSpec({ deltaSpeedMPH: Math.abs(value) });
            }}
            disabled={disabled}
            size="1"
          />
        </>
      )}

      {showPrecisionInputs && safePriority === BuildPriority.Breaks && (
        <>
          <Flex gridColumn="span 4" justify="between" align="center">
            <CommonInputLabel
              label={
                machine.isMetric
                  ? 'tr.max-breaks-delta-cm'
                  : 'tr.max-breaks-delta-inches'
              }
            />

            {checkThresholds &&
              spec.deltaBreaksInches < THRESHOLD_BREAKS_IN && (
                <WarningIcon
                  content={
                    `The threshold for breaks that you have entered might be difficult or impossible to reproduce. ` +
                    `Please consider increasing breaks delta to at least ${Math.ceil(
                      THRESHOLD_BREAKS_IN * unitFactors.fromBreaksIn
                    ).toFixed(0)} ` +
                    `${MetricHelper.getBreaksUnit(machine.isMetric)}.`
                  }
                />
              )}
          </Flex>

          <CommonTextInput
            id="precision-training-max-breaks"
            type="number"
            name="deltaBreaksInches"
            value={breaksText.toString()}
            title="Provide a positive number"
            onChange={(v) => setBreaksText(v ?? '')}
            onNumericChange={(v) => {
              // * this conversion is in reverse b/c the user is inputting in their own units
              const value = v / unitFactors.fromBreaksIn;
              updateSpec({ deltaBreaksInches: Math.abs(value) });
            }}
            disabled={disabled}
            size="1"
          />
        </>
      )}

      {showPrecisionInputs &&
        [BuildPriority.Spins, BuildPriority.Default].includes(safePriority) && (
          <>
            <Flex gridColumn="span 4" justify="between" align="center">
              <CommonInputLabel label="tr.max-spins-delta-rpm" />

              {checkThresholds && spec.deltaSpinsRPM < THRESHOLD_SPINS_RPM && (
                <WarningIcon
                  content={
                    `The threshold for spins that you have entered might be difficult or impossible to reproduce. ` +
                    `Please consider increasing spins delta to at least ${THRESHOLD_SPINS_RPM}rpm.`
                  }
                />
              )}
            </Flex>

            <CommonTextInput
              id="precision-training-max-spin"
              type="number"
              name="deltaSpinsRPM"
              value={spec.deltaSpinsRPM.toString()}
              title="Provide a positive number"
              onNumericChange={(v) => {
                updateSpec({ deltaSpinsRPM: Math.abs(v) });
              }}
              disabled={disabled}
              size="1"
            />
          </>
        )}

      {props.preset.showSmartSamplingToggle && (
        <>
          <Flex gridColumn="span 4" align="center">
            <CommonInputLabel label="tr.smart-sampling" />
          </Flex>

          <CommonSwitchInput
            id="precision-training-smart-sampling"
            name="smart-sampling"
            title="tr.smart-sampling-msg"
            defaultChecked={spec.smartSampling}
            onCheckedChange={(v) => {
              updateSpec({ smartSampling: v });
            }}
            disabled={disabled}
            size="1"
          />
        </>
      )}
    </CommonFormGrid>
  );
};
